// Le MAGASIN côté navigateur : la seule source de vérité de l'interface.
//  - `etatConfirme` + `boiteEnvoi` → `etat` affiché (= réducteurs rejoués, docs/architecture.md §4.2) ;
//  - émission d'événements (`emettre`), synchronisation (`synchroniser`), contenu (`chargerContenu`) ;
//  - un mécanisme d'abonnement minimal pour React (`useSyncExternalStore`, voir hooks.ts).
// Tout ce qui touche à l'état passe par ici : les écrans ne modifient jamais l'état directement.
import { useSyncExternalStore } from 'react';
import type { Etat } from '@partage/types';
import { etatInitial, normaliserEtat } from '@partage/types';
import type { Evenement, ReponseSync } from '@partage/evenements';
import { validerEvenement } from '@partage/evenements';
import { reduireTout, appliquer } from '@partage/reducteurs';
import type { BundleParcours, BundleSaison, CatalogueContenu } from '@partage/contenu';
import { api, ErreurApi, estHorsLigne } from '../api';
import { baseLocale } from './base-locale';

export type StatutSync = 'a_jour' | 'en_attente' | 'synchronisation' | 'hors_ligne' | 'reconnexion' | 'erreur';

export interface Instantane {
  pret: boolean;
  /** Email de l'utilisateur connu localement (undefined = jamais connecté sur cet appareil). */
  utilisateur?: string;
  /** Vrai quand le serveur a confirmé la session dans cette « vie » de la page. */
  connecte: boolean;
  etat: Etat;
  sequence: number;
  enAttente: number;
  statutSync: StatutSync;
  derniereSync?: string;
  erreurSync?: string;
  bundles: Record<number, BundleSaison>;
  /** Parcours annexes déjà téléchargés (R-PAR-01). */
  parcours: Record<string, BundleParcours>;
  catalogue?: CatalogueContenu;
  appareil: string;
}

type Ecouteur = () => void;

class Magasin {
  private instantane: Instantane;
  private etatConfirme: Etat = etatInitial();
  private boiteEnvoi: Evenement[] = [];
  private ecouteurs = new Set<Ecouteur>();
  private syncEnCours?: Promise<void>;
  private minuteur?: number;

  constructor() {
    this.instantane = { pret: false, connecte: false, etat: etatInitial(), sequence: 0, enAttente: 0, statutSync: 'a_jour', bundles: {}, parcours: {}, appareil: '' };
  }

  // ---- abonnement React ----
  abonner = (ecouteur: Ecouteur) => { this.ecouteurs.add(ecouteur); return () => { this.ecouteurs.delete(ecouteur); }; };
  lire = () => this.instantane;
  private publier(changements: Partial<Instantane>) {
    this.instantane = { ...this.instantane, ...changements };
    for (const e of this.ecouteurs) e();
  }
  private recalculer() {
    const etat = reduireTout(this.etatConfirme, this.boiteEnvoi);
    this.publier({ etat, enAttente: this.boiteEnvoi.length, statutSync: this.boiteEnvoi.length && this.instantane.statutSync === 'a_jour' ? 'en_attente' : this.instantane.statutSync });
  }

  // ---- démarrage ----
  async demarrer(): Promise<void> {
    let appareil = await baseLocale.lireMeta<string>('appareil');
    if (!appareil) { appareil = `nav-${crypto.randomUUID().slice(0, 8)}`; await baseLocale.ecrireMeta('appareil', appareil); }
    const [etat, boite, sequence, utilisateur, derniereSync, bundles, parcours] = await Promise.all([
      baseLocale.lireEtat(), baseLocale.lireBoiteEnvoi(), baseLocale.lireMeta<number>('sequence'),
      baseLocale.lireMeta<string>('utilisateur'), baseLocale.lireMeta<string>('derniere_sync'), baseLocale.listerContenu(),
      baseLocale.listerParcours(),
    ]);
    // L'état gardé dans IndexedDB peut dater d'avant un champ ajouté au modèle : un appareil
    // resté hors ligne ne bénéficie d'aucun rejeu côté serveur.
    this.etatConfirme = etat ? normaliserEtat(etat) : etatInitial();
    this.boiteEnvoi = boite;
    this.publier({ pret: true, appareil, utilisateur, sequence: sequence ?? 0, derniereSync, bundles: Object.fromEntries(bundles.map((b) => [b.numero, b])), parcours: Object.fromEntries(parcours.map((p) => [p.id, p])), statutSync: navigator.onLine ? 'a_jour' : 'hors_ligne' });
    this.recalculer();

    window.addEventListener('online', () => { this.publier({ statutSync: this.boiteEnvoi.length ? 'en_attente' : 'a_jour' }); void this.synchroniser(); });
    window.addEventListener('offline', () => this.publier({ statutSync: 'hors_ligne' }));
    this.minuteur = window.setInterval(() => { if (this.boiteEnvoi.length && navigator.onLine) void this.synchroniser(); }, 60_000);
    await this.verifierSession();
  }

  /** Demande au serveur qui je suis ; hors ligne, on garde l'utilisateur local (R-OFF-01). */
  async verifierSession(): Promise<boolean> {
    try {
      const moi = await api<{ email: string }>('/api/auth/moi');
      await baseLocale.ecrireMeta('utilisateur', moi.email);
      this.publier({ utilisateur: moi.email, connecte: true });
      void this.synchroniser();
      void this.chargerContenu();
      return true;
    } catch (erreur) {
      if (erreur instanceof ErreurApi && erreur.statut === 401) {
        this.publier({ connecte: false, statutSync: this.instantane.utilisateur ? 'reconnexion' : 'a_jour' });
      } else if (estHorsLigne(erreur)) {
        this.publier({ statutSync: 'hors_ligne' });
      }
      return false;
    }
  }

  // ---- authentification ----
  async connexion(email: string, motDePasse: string, resterConnecte: boolean): Promise<void> {
    await api('/api/auth/connexion', { corps: { email, mot_de_passe: motDePasse, rester_connecte: resterConnecte } });
    // Si l'appareil contenait les données d'un AUTRE compte, on repart de zéro.
    if (this.instantane.utilisateur && this.instantane.utilisateur !== email.toLowerCase()) {
      await baseLocale.toutEffacer();
      this.etatConfirme = etatInitial(); this.boiteEnvoi = [];
      this.publier({ sequence: 0, bundles: {}, parcours: {} });
    }
    await baseLocale.ecrireMeta('utilisateur', email.toLowerCase());
    this.publier({ utilisateur: email.toLowerCase(), connecte: true, statutSync: this.boiteEnvoi.length ? 'en_attente' : 'a_jour' });
    if (this.instantane.sequence === 0 && this.boiteEnvoi.length === 0) await this.chargerInstantane();
    await this.synchroniser();
    await this.chargerContenu();
  }

  async deconnexion(): Promise<void> {
    try { await api('/api/auth/deconnexion', { methode: 'POST' }); } catch { /* hors ligne : la session tombera d'elle-même */ }
    this.publier({ connecte: false, statutSync: 'reconnexion' });
  }

  /** Un nouvel appareil récupère l'instantané complet plutôt que tous les événements. */
  private async chargerInstantane(): Promise<void> {
    const r = await api<{ etat: Etat; sequence: number }>('/api/etat');
    this.etatConfirme = normaliserEtat(r.etat);
    await baseLocale.ecrireEtat(r.etat);
    await baseLocale.ecrireMeta('sequence', r.sequence);
    this.publier({ sequence: r.sequence });
    this.recalculer();
  }

  // ---- événements ----
  /** Crée un événement, l'applique localement tout de suite, le met en boîte d'envoi (R-OFF-03). */
  async emettre<T extends Evenement['type']>(type: T, charge: Extract<Evenement, { type: T }>['charge']): Promise<Evenement> {
    const ev = { id: crypto.randomUUID(), type, charge, cree_le: new Date().toISOString(), appareil: this.instantane.appareil, version_schema: 1 as const } as Evenement;
    const v = validerEvenement(ev);
    if (!v.ok) throw new Error(`Événement invalide (${type}) : ${v.raison}`);
    // D'abord l'état affiché (synchrone : l'interface réagit immédiatement), puis la persistance.
    this.boiteEnvoi.push(ev);
    this.recalculer();
    await baseLocale.ajouterALaBoite(ev);
    if (navigator.onLine && this.instantane.connecte) void this.synchroniser();
    return ev;
  }

  // ---- synchronisation (docs/architecture.md §4.4) ----
  synchroniser(): Promise<void> {
    if (this.syncEnCours) return this.syncEnCours;
    this.syncEnCours = this.executerSync().finally(() => { this.syncEnCours = undefined; });
    return this.syncEnCours;
  }

  private async executerSync(): Promise<void> {
    if (!navigator.onLine) { this.publier({ statutSync: 'hors_ligne' }); return; }
    if (!this.instantane.utilisateur) return;
    this.publier({ statutSync: 'synchronisation', erreurSync: undefined });
    try {
      const lot = this.boiteEnvoi.slice(0, 500);
      const r = await api<ReponseSync>('/api/sync', { corps: { appareil: this.instantane.appareil, depuis_sequence: this.instantane.sequence, evenements: lot } });
      // 1. Les événements refusés sont mis de côté, jamais rejoués silencieusement (R-OFF-07).
      for (const rejet of r.rejetes) {
        const ev = this.boiteEnvoi.find((e) => e.id === rejet.id);
        if (ev) await baseLocale.ajouterRejete(ev, rejet.raison);
      }
      // 2. On rejoue les nouveaux (dans l'ordre du serveur) sur l'état confirmé.
      for (const ev of r.nouveaux) appliquer(this.etatConfirme, ev);
      // 3. Les acquittés et les rejetés quittent la boîte d'envoi.
      const partis = new Set([...r.acquittes, ...r.rejetes.map((x) => x.id)]);
      this.boiteEnvoi = this.boiteEnvoi.filter((e) => !partis.has(e.id));
      await baseLocale.retirerDeLaBoite([...partis]);
      await baseLocale.ecrireEtat(this.etatConfirme);
      await baseLocale.ecrireMeta('sequence', r.sequence);
      const maintenant = new Date().toISOString();
      await baseLocale.ecrireMeta('derniere_sync', maintenant);
      this.publier({ sequence: r.sequence, connecte: true, derniereSync: maintenant, statutSync: this.boiteEnvoi.length ? 'en_attente' : 'a_jour' });
      this.recalculer();
      if (this.boiteEnvoi.length) void this.synchroniserBientot();
    } catch (erreur) {
      if (erreur instanceof ErreurApi && erreur.statut === 401) this.publier({ connecte: false, statutSync: 'reconnexion' });
      else if (estHorsLigne(erreur)) this.publier({ statutSync: 'hors_ligne' });
      else this.publier({ statutSync: 'erreur', erreurSync: (erreur as Error).message });
    }
  }

  private synchroniserBientot() {
    window.setTimeout(() => void this.synchroniser(), 500);
  }

  // ---- contenu (R-OFF-02) ----
  async chargerContenu(): Promise<void> {
    try {
      const catalogue = await api<CatalogueContenu>('/api/contenu');
      this.publier({ catalogue });
      const courante = this.instantane.etat.saison_courante;
      for (const numero of [courante, courante + 1]) {
        const dispo = catalogue.saisons.find((s) => s.numero === numero);
        if (!dispo) continue;
        const local = this.instantane.bundles[numero];
        if (local && local.empreinte === dispo.empreinte) continue;
        const bundle = await api<BundleSaison | undefined>(`/api/contenu/saison/${numero}?empreinte=${local?.empreinte ?? ''}`);
        if (bundle) {
          await baseLocale.ecrireContenu(bundle);
          this.publier({ bundles: { ...this.instantane.bundles, [numero]: bundle } });
        }
      }
      // R-OFF-02 : les parcours annexes ACTIVÉS sont mis en cache comme les saisons. Les autres
      // n'existent dans le catalogue que par leur titre, ce qui suffit à les proposer.
      for (const { id, empreinte } of catalogue.parcours ?? []) {
        if (!this.instantane.etat.parcours[id]) continue;
        const local = this.instantane.parcours[id];
        if (local && local.empreinte === empreinte) continue;
        const bundle = await api<BundleParcours | undefined>(`/api/contenu/parcours/${id}?empreinte=${local?.empreinte ?? ''}`);
        if (bundle) {
          await baseLocale.ecrireParcours(bundle);
          this.publier({ parcours: { ...this.instantane.parcours, [id]: bundle } });
        }
      }
    } catch { /* hors ligne ou non connecté : on garde le contenu local */ }
  }

  arreter() { if (this.minuteur) clearInterval(this.minuteur); }
}

export const magasin = new Magasin();

/** Hook React : `const { etat, statutSync } = useMagasin();` */
export function useMagasin(): Instantane {
  return useSyncExternalStore(magasin.abonner, magasin.lire, magasin.lire);
}
